import React, { useState, useEffect } from 'react';
import { Check, Warning, ShieldCheck, Drop, ListBullets, CookingPot } from '@phosphor-icons/react';
import { NUTRITION, SUPPLEMENTS, FOODS_EAT, FOODS_AVOID, DAILY_MEALS, getMealPlanKey, generateMealPrep } from '../data/programData';
import { api } from '../data/api';

export default function Nutrition({ user, phase, today, mealLogs, updateMealLog, showToast, customProgram }) {
  // Resolve nutrition data from custom program or static protocol
  const isCustom = !!(customProgram?.nutrition);
  const calPlan = user.cal_plan === 2 ? 'opt2' : 'opt1';
  const nutData = isCustom ? null : NUTRITION[calPlan];
  const phaseNut = isCustom
    ? { cal: customProgram.nutrition.daily_calories, p: customProgram.nutrition.protein_g, c: customProgram.nutrition.carbs_g, f: customProgram.nutrition.fat_g }
    : (phase <= 1 ? nutData.phases[0] : phase <= 3 ? nutData.phases[1] : nutData.phases[2]);
  const mealKey = isCustom ? null : getMealPlanKey(phase);
  const dailyMeals = isCustom
    ? (customProgram.nutrition.meals || []).map((m, i) => ({ id: i, name: m.name, time: m.time || '', items: m.items || [] }))
    : DAILY_MEALS[mealKey];

  const todayLog = mealLogs[today] || {};
  const checkedMeals = todayLog.meals || {};
  const [suppLogs, setSuppLogs] = useState({});
  const [waterGlasses, setWaterGlasses] = useState(0);
  const [subTab, setSubTab] = useState('meals');

  useEffect(() => {
    api.getSupplements(user.user_id, today).then(logs => {
      if (logs.length > 0) setSuppLogs(logs[0].supplements || {});
    }).catch(() => {});
    api.getWater(user.user_id, today).then(logs => {
      if (logs.length > 0) setWaterGlasses(logs[0].glasses || 0);
    }).catch(() => {});
  }, [user.user_id, today]);

  const toggleMeal = (idx) => {
    const updated = { ...checkedMeals, [idx]: !checkedMeals[idx] };
    updateMealLog(today, updated);
    const meal = dailyMeals[idx];
    if (showToast) showToast(updated[idx] ? `${meal.name} logged` : `${meal.name} unchecked`, 'success');
  };

  const toggleSupp = async (id) => {
    const updated = { ...suppLogs, [id]: !suppLogs[id] };
    setSuppLogs(updated);
    await api.saveSupplements({ user_id: user.user_id, date: today, supplements: updated });
    if (showToast) showToast(updated[id] ? 'Supplement taken' : 'Supplement unchecked', 'success');
  };

  const updateWater = async (glasses) => {
    const val = Math.max(0, Math.min(20, glasses));
    setWaterGlasses(val);
    await api.saveWater({ user_id: user.user_id, date: today, glasses: val });
    if (val >= 16 && showToast) showToast('4L target hit — hydrated', 'fire');
  };

  const mealsEaten = Object.values(checkedMeals).filter(Boolean).length;
  const waterMl = waterGlasses * 250;
  const waterPct = Math.min(100, (waterMl / 4000) * 100);

  // Calculate today's totals from checked meals
  let eatenCal = 0, eatenP = 0, eatenC = 0, eatenF = 0;
  dailyMeals.forEach((meal, i) => {
    if (checkedMeals[i]) {
      meal.items.forEach(item => {
        eatenCal += item.cal;
        eatenP += item.p;
        eatenC += item.c;
        eatenF += item.f;
      });
    }
  });

  const macroRing = (current, target, color, label) => {
    const ratio = target > 0 ? Math.min(current / target, 1) : 0;
    const circumference = 2 * Math.PI * 40;
    return (
      <div className="flex flex-col items-center">
        <svg viewBox="0 0 100 100" className="w-20 h-20">
          <circle cx="50" cy="50" r="40" fill="none" stroke="#1A1A1A" strokeWidth="8" />
          <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${ratio * circumference} ${circumference}`} transform="rotate(-90 50 50)" />
          <text x="50" y="44" textAnchor="middle" fill="white" fontFamily="JetBrains Mono" fontSize="12" fontWeight="bold">{current}g</text>
          <text x="50" y="58" textAnchor="middle" fill="#525252" fontSize="9">/{target}g</text>
        </svg>
        <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mt-1">{label}</span>
      </div>
    );
  };

  const mealPrep = generateMealPrep(phase);

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="nutrition-page">
      {/* Header */}
      <div className="glass rounded-xl p-3 sm:p-4">
        <div className="font-head text-lg sm:text-xl font-bold uppercase tracking-tight text-white">Nutrition</div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mt-0.5">
          {eatenCal > 0 ? `${eatenCal} cal eaten today` : 'Track meals, water & supplements'}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar pb-0.5">
        {[
          { id: 'meals', label: 'Meals', Icon: ListBullets },
          { id: 'water', label: 'Water', Icon: Drop },
          { id: 'prep', label: 'Prep', Icon: CookingPot },
          { id: 'supps', label: 'Supps', Icon: ShieldCheck },
          { id: 'eat', label: 'Eat', Icon: Check },
          { id: 'avoid', label: 'Avoid', Icon: Warning },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex-shrink-0 flex items-center gap-1 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs font-bold uppercase border transition-all active:scale-95 ${
              subTab === t.id ? 'bg-brand text-white border-brand' : 'bg-elevated text-neutral-500 border-border'
            }`}
            data-testid={`nutrition-tab-${t.id}`}
          >
            <t.Icon size={14} weight="bold" /> {t.label}
          </button>
        ))}
      </div>

      {/* Phase header with macros */}
      <div className="glass rounded-xl p-4" style={{ borderLeftColor: '#FF3B30', borderLeftWidth: '4px' }}>
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="font-head text-lg font-bold uppercase text-white">{phaseNut.phase}</div>
            <div className="text-xs text-neutral-500">{nutData.label}</div>
          </div>
          <div className="font-mono text-2xl font-bold text-warning">{phaseNut.cal.toLocaleString()}<span className="text-xs text-neutral-500 ml-1">kcal</span></div>
        </div>
        <div className="flex justify-around">
          {macroRing(eatenP, phaseNut.p, '#FF3B30', 'Protein')}
          {macroRing(eatenC, phaseNut.c, '#007AFF', 'Carbs')}
          {macroRing(eatenF, phaseNut.f, '#32D74B', 'Fat')}
        </div>
        <div className="text-center mt-2">
          <span className={`inline-block px-3 py-1 rounded-full font-mono text-xs font-bold ${
            eatenCal > phaseNut.cal ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-success/20 text-success border border-success/30'
          }`}>
            {eatenCal} / {phaseNut.cal} kcal ({eatenCal > phaseNut.cal ? '+' : ''}{eatenCal - phaseNut.cal})
          </span>
        </div>
      </div>

      {/* === MEALS TAB === */}
      {subTab === 'meals' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="font-head text-lg font-bold uppercase text-white">Today's Meals</div>
            <span className="font-mono text-sm text-neutral-400">{mealsEaten}/{dailyMeals.length}</span>
          </div>
          {dailyMeals.map((meal, idx) => {
            const isChecked = checkedMeals[idx];
            const mealCal = meal.items.reduce((s, it) => s + it.cal, 0);
            const mealP = meal.items.reduce((s, it) => s + it.p, 0);
            return (
              <div
                key={idx}
                className={`bg-surface border rounded-md overflow-hidden transition-all ${isChecked ? 'border-success/40' : 'border-border'}`}
                data-testid={`meal-card-${idx}`}
              >
                <button
                  onClick={() => toggleMeal(idx)}
                  className="w-full p-4 flex items-center gap-3 active:bg-elevated transition-colors"
                  data-testid={`meal-toggle-${idx}`}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-md border-2 flex-shrink-0 transition-all ${
                    isChecked ? 'bg-success border-success' : 'border-border'
                  }`}>
                    {isChecked && <Check size={24} weight="bold" className="text-black" />}
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`font-head text-base font-bold uppercase ${isChecked ? 'line-through text-neutral-500' : 'text-white'}`}>
                        {meal.name}
                      </span>
                      <span className="font-mono text-sm text-warning font-bold ml-2">{mealCal} kcal</span>
                    </div>
                    <div className="text-[10px] text-neutral-500">{meal.time} / P:{mealP}g</div>
                  </div>
                </button>
                {/* Food items breakdown */}
                <div className="border-t border-border px-4 py-3 space-y-1.5">
                  {meal.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-neutral-400">{item.food} <span className="text-neutral-600">{item.qty}</span></span>
                      <div className="flex gap-3 text-[10px] font-mono">
                        <span className="text-neutral-500">{item.cal}cal</span>
                        <span className="text-red-400">P{item.p}</span>
                        <span className="text-blue-400">C{item.c}</span>
                        <span className="text-green-400">F{item.f}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* === WATER TRACKER TAB === */}
      {subTab === 'water' && (
        <div className="glass rounded-xl p-6 space-y-6" data-testid="water-tracker">
          <div className="text-center">
            <div className="font-head text-2xl font-bold uppercase text-white">Water Intake</div>
            <div className="text-xs text-neutral-500 mt-1">Target: 4L (16 glasses) per day</div>
          </div>

          {/* Big water display */}
          <div className="flex flex-col items-center">
            <div className="relative w-40 h-40 mb-4">
              <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
                <circle cx="80" cy="80" r="70" fill="none" stroke="#1A1A1A" strokeWidth="10" />
                <circle cx="80" cy="80" r="70" fill="none" stroke="#007AFF" strokeWidth="10"
                  strokeDasharray={`${waterPct * 4.4} 440`} strokeLinecap="butt" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-mono text-4xl font-bold text-white" data-testid="water-amount">{waterMl}</span>
                <span className="text-xs text-neutral-500">/ 4000 ml</span>
              </div>
            </div>

            <div className="font-mono text-lg text-accent font-bold">{waterGlasses} glasses</div>
          </div>

          {/* Glass grid */}
          <div className="grid grid-cols-8 gap-2" data-testid="water-glass-grid">
            {[...Array(16)].map((_, i) => (
              <button
                key={i}
                onClick={() => updateWater(i + 1)}
                className={`aspect-square rounded-md border flex items-center justify-center text-lg transition-all active:scale-90 ${
                  i < waterGlasses
                    ? 'bg-accent/30 border-accent/50 text-accent'
                    : 'bg-elevated border-border text-neutral-700'
                }`}
                data-testid={`water-glass-${i + 1}`}
              >
                <Drop size={18} weight={i < waterGlasses ? 'fill' : 'regular'} />
              </button>
            ))}
          </div>

          {/* Quick add/remove buttons */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => updateWater(waterGlasses - 1)}
              className="w-20 h-16 flex items-center justify-center glass-elevated rounded-xl text-white text-2xl font-bold active:scale-95"
              data-testid="water-minus-btn"
            >
              -
            </button>
            <button
              onClick={() => updateWater(waterGlasses + 1)}
              className="w-20 h-16 flex items-center justify-center bg-accent border border-accent rounded-md text-white text-2xl font-bold active:scale-95"
              data-testid="water-plus-btn"
            >
              +
            </button>
            <button
              onClick={() => updateWater(0)}
              className="w-20 h-16 flex items-center justify-center glass-elevated rounded-xl text-neutral-500 text-xs font-bold uppercase active:scale-95"
              data-testid="water-reset-btn"
            >
              Reset
            </button>
          </div>

          {waterPct >= 100 && (
            <div className="text-center p-3 bg-success/10 border border-success/30 rounded-md">
              <div className="text-success font-bold uppercase text-sm">4L Target Hit!</div>
            </div>
          )}
        </div>
      )}

      {/* === MEAL PREP TAB === */}
      {subTab === 'prep' && (
        <div className="space-y-4" data-testid="meal-prep-section">
          <div className="glass rounded-xl p-4">
            <div className="font-head text-lg font-bold uppercase text-white mb-1">Weekly Meal Prep</div>
            <div className="text-xs text-neutral-500 mb-4">Shopping list based on your {phaseNut.phase} meal plan (6 days)</div>

            <div className="space-y-2">
              {mealPrep.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 glass-elevated rounded-xl">
                  <div>
                    <div className="text-sm text-white font-medium">{item.food}</div>
                    <div className="text-[10px] text-neutral-500">{item.calPerServing} cal / {item.proteinPerServing}g protein per serving</div>
                  </div>
                  <div className="font-mono text-sm text-warning font-bold text-right">{item.weeklyQty}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Prep tips */}
          <div className="glass rounded-xl p-4">
            <div className="font-head text-sm font-bold uppercase text-white mb-3">Prep Day Tips</div>
            {[
              "Cook all protein (chicken, beef, fish) in bulk — portion into 6 containers",
              "Pre-cook rice/sweet potato/quinoa for the week — refrigerate in portions",
              "Wash + chop all vegetables — store in airtight containers",
              "Pre-mix post-workout shakes (dry ingredients) into individual bags",
              "Portion out almonds/walnuts into small bags for daily snacks",
              "Set 6 overnight oat jars for the week (oats + protein + banana)",
            ].map((tip, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <span className="text-brand font-mono text-xs font-bold mt-0.5">{i + 1}.</span>
                <span className="text-xs text-neutral-300 leading-relaxed">{tip}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === SUPPLEMENTS TAB === */}
      {subTab === 'supps' && (
        <div className="glass rounded-xl p-4 space-y-3">
          <div className="font-head text-lg font-bold uppercase text-white">Daily Supplements</div>
          {SUPPLEMENTS.map(sup => {
            const checked = suppLogs[sup.id];
            return (
              <button key={sup.id} onClick={() => toggleSupp(sup.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-md border transition-all active:scale-[0.98] ${
                  checked ? 'bg-success/10 border-success/30' : 'bg-elevated border-border'
                }`} data-testid={`supp-${sup.id}`}>
                <div className={`w-8 h-8 flex items-center justify-center rounded border-2 ${
                  checked ? 'bg-success border-success' : 'border-neutral-600'
                }`}>{checked && <Check size={14} weight="bold" className="text-black" />}</div>
                <div className="text-left">
                  <div className="font-bold text-sm text-white">{sup.name}</div>
                  <div className="text-[10px] text-neutral-500">{sup.dosage}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Eat / Avoid tabs */}
      {subTab === 'eat' && (
        <div className="glass rounded-xl p-4 space-y-3">
          <div className="font-head text-lg font-bold uppercase text-white flex items-center gap-2">
            <ShieldCheck size={22} weight="bold" className="text-success" /> Eat This
          </div>
          {FOODS_EAT.map((food, i) => (
            <div key={i} className="p-3 glass-elevated rounded-xl text-sm text-neutral-300 leading-relaxed">{food}</div>
          ))}
        </div>
      )}
      {subTab === 'avoid' && (
        <div className="glass rounded-xl p-4 space-y-3">
          <div className="font-head text-lg font-bold uppercase text-white flex items-center gap-2">
            <Warning size={22} weight="bold" className="text-brand" /> Avoid
          </div>
          {FOODS_AVOID.map((food, i) => (
            <div key={i} className="p-3 bg-elevated border border-brand/20 rounded-md text-sm text-neutral-300 leading-relaxed">{food}</div>
          ))}
        </div>
      )}
    </div>
  );
}
